import { networkRelativeTime, useSystemOperations } from "../../i18n/systemOperationsPresentation";
import { updatesText as up, updatesLocale, type UpdatesKey } from "../../i18n/updatesPresentation";
import { phpPoolState, type PhpPoolState, type PhpSecurityStatus } from "../../hooks/usePhpSecurityStatus";
import { cn } from "../../utils/cn";
import { Tooltip } from "../ui/Tooltip";

/** Product names for the app ids the CLI records; an unknown id is shown as recorded. */
const PHP_APP_NAMES: Readonly<Record<string, string>> = {
  rutorrent: "ruTorrent",
  phpmyadmin: "phpMyAdmin",
  nextcloud: "Nextcloud",
};

const POOL_STATE: Record<PhpPoolState, { label: UpdatesKey; dot: string }> = {
  ok: { label: "phpAtFloor", dot: "bg-emerald-500" },
  below: { label: "phpBelowFloor", dot: "bg-red-500" },
  unreadable: { label: "phpVersionUnreadable", dot: "bg-yellow-500" },
};

type PhpSecurityStatusBlockProps = {
  status: PhpSecurityStatus | null;
  loading: boolean;
  failed: boolean;
};

/** Each PHP app's installed version against its security floor, for the maintenance confirm view. */
export const PhpSecurityStatusBlock = ({ status, loading, failed }: PhpSecurityStatusBlockProps) => {
  const { op } = useSystemOperations();
  const checkedSec = status?.checkedAt ? Date.parse(status.checkedAt) / 1000 : Number.NaN;
  const checkedLabel = Number.isFinite(checkedSec) ? up("phpChecked", { time: networkRelativeTime(checkedSec, updatesLocale(), op) }) : null;

  const notice = failed
    ? up("phpLoadFailed")
    : !status
      ? (loading ? up("phpLoading") : null)
      : status.checkedAt === null
        ? up("phpNotChecked")
        : status.pools.length === 0
          ? up("phpNoApps")
          : null;

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <p className="text-xs font-medium text-foreground">{up("phpSecurity")}</p>
        {!failed && checkedLabel && <p className="text-[10px] text-muted-foreground">{checkedLabel}</p>}
      </div>
      {notice ? (
        <p className="border-t border-white/[0.06] px-3 py-2 text-[10px] text-muted-foreground">{notice}</p>
      ) : status && status.pools.length > 0 && (
        <ul className="divide-y divide-white/[0.06] border-t border-white/[0.06]">
          {status.pools.map((pool) => {
            const state = POOL_STATE[phpPoolState(pool)];
            const stateLabel = up(state.label);
            return (
              <li key={`${pool.app}-${pool.major}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Tooltip content={stateLabel}>
                    <span aria-hidden="true" className={cn("inline-block h-2 w-2 flex-shrink-0 rounded-full", state.dot)} />
                  </Tooltip>
                  <span className="sr-only">{stateLabel}</span>
                  <span className="text-xs font-medium text-foreground">{PHP_APP_NAMES[pool.app] ?? pool.app}</span>
                  <span className="text-[10px] text-muted-foreground">{up("phpMajor", { major: pool.major })}</span>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground sm:ml-auto">
                  <code className="break-all font-mono text-[11px] text-foreground/90">{pool.installed ?? up("phpVersionUnreadable")}</code>
                  <span className="break-all">{up("phpFloor", { version: pool.floor })}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
